import {
  decideKybStep,
  fetchKybReview,
  kybDocumentLabels,
  KybStep,
  rejectKybOwner,
} from "api/admin/kycReviews";
import useAdminAccess from "hooks/useAdminAccess";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Applicant,
  dateLabel,
  DecisionButtons,
  DecisionForm,
  reviewKey,
  ReviewError,
  ReviewFields,
  ReviewLayout,
  ReviewLoading,
  ReviewSection,
  StatusBadge,
} from "./ReviewComponents";

const sectionLabels = {
  business_details: "business details",
  business_documents: "business documents",
  ubo: "beneficial owners",
};
type Decision =
  | { action: "approve" | "reject"; step: KybStep }
  | { action: "reject"; ownerID: number; ownerName: string };

const documentUrl = (value: string | null) => {
  if (!value) return null;
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

export default function KybReview() {
  const { userID = "" } = useParams();
  return <KybReviewForUser key={userID} userID={userID} />;
}

function KybReviewForUser({ userID }: { userID: string }) {
  const { admin, can } = useAdminAccess();
  const client = useQueryClient();
  const key = [...reviewKey("kyb", admin?.id), "detail", userID];
  const query = useQuery(key, ({ signal }) => fetchKybReview(userID, signal), {
    enabled: can("users.kyc.view") && /^\d+$/.test(userID),
    retry: false,
  });
  const [decision, setDecision] = useState<Decision | null>(null);
  const mutation = useMutation(
    ({ choice, reason }: { choice: Decision; reason: string }) =>
      "ownerID" in choice
        ? rejectKybOwner(userID, choice.ownerID, reason)
        : decideKybStep(userID, choice.step, choice.action, reason),
    {
      onSuccess: (detail) => {
        client.setQueryData(key, detail);
        setDecision(null);
        toast.success("Tier 4 review saved.");
        client.invalidateQueries(["kycReviews", "kyb"]);
      },
      onError: () => {
        query.refetch();
      },
    },
  );
  const data = query.data;
  const canDecide =
    can("users.kyc.approve") && data?.tier4Status !== "completed";
  const busy = query.isFetching || mutation.isLoading;
  const selectedSectionStatus =
    decision && "step" in decision && data
      ? {
          business_details: data.businessDetailsStatus,
          business_documents: data.businessDocumentsStatus,
          ubo: data.uboStatus,
        }[decision.step]
      : null;
  const decisionChanged = Boolean(
    decision &&
    data &&
    (data.tier4Status === "completed" ||
      ("step" in decision
        ? selectedSectionStatus !== "pending"
        : !data.owners.some((owner) => owner.id === decision.ownerID))),
  );
  const choose = (choice: Decision) => {
    mutation.reset();
    setDecision(choice);
  };
  const decisionForm = (choice: Decision) => (
    <DecisionForm
      title={
        "ownerID" in choice
          ? `Reject verification for ${choice.ownerName}`
          : `${choice.action === "approve" ? "Approve" : "Reject"} ${sectionLabels[choice.step]}`
      }
      description={
        "ownerID" in choice
          ? "The owner must repeat identity and address verification. The applicant must resubmit the beneficial-owner section."
          : choice.action === "approve"
            ? "This approves this section. When all three sections are approved, the account upgrades to Tier 4."
            : "The customer will receive this reason and can correct and resubmit this section."
      }
      rejecting={choice.action === "reject"}
      pending={mutation.isLoading}
      disabled={busy || !canDecide}
      error={mutation.error}
      onCancel={() => setDecision(null)}
      onSubmit={(reason) => mutation.mutateAsync({ choice, reason })}
    />
  );
  const sectionActions = (step: KybStep, status: string) =>
    canDecide && status === "pending" ? (
      <>
        {!decision && (
          <DecisionButtons
            title={sectionLabels[step]}
            disabled={busy}
            choose={(action) => choose({ action, step })}
          />
        )}
        {decision &&
          "step" in decision &&
          decision.step === step &&
          decisionForm(decision)}
      </>
    ) : null;
  return (
    <ReviewLayout kind="kyb" detail>
      {query.isLoading ? (
        <ReviewLoading />
      ) : query.isError ? (
        <ReviewError error={query.error} retry={() => query.refetch()} />
      ) : data ? (
        <>
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <StatusBadge status={data.tier4Status} />
            <span className="text-sm text-gray-500">
              Application #{data.id} · Updated {dateLabel(data.updatedAt)}
            </span>
            <button
              type="button"
              onClick={() => query.refetch()}
              disabled={busy}
              className="text-sm underline disabled:opacity-50"
            >
              Refresh review and document links
            </button>
          </div>
          <Applicant user={data.user} userID={data.userID} />
          {decisionChanged && (
            <div
              role="alert"
              className="p-4 mb-5 text-sm border border-orange-200 rounded bg-orange-50"
            >
              This review changed. Check the latest section statuses before
              making another decision.
              <button
                type="button"
                onClick={() => setDecision(null)}
                disabled={mutation.isLoading}
                className="ml-3 underline"
              >
                Close previous decision
              </button>
            </div>
          )}
          <ReviewSection
            title="1. Business details"
            status={data.businessDetailsStatus}
          >
            <ReviewFields
              fields={[
                ["Business name", data.businessName],
                [
                  data.requirements?.registrationNumberLabel ??
                    "Company registration number",
                  data.cacRegistrationNumber,
                ],
                ["Business address", data.businessAddress],
              ]}
            />
            {sectionActions("business_details", data.businessDetailsStatus)}
          </ReviewSection>
          <ReviewSection
            title="2. Business documents"
            status={data.businessDocumentsStatus}
          >
            <ul className="divide-y divide-gray-100">
              {(
                Object.keys(
                  kybDocumentLabels,
                ) as (keyof typeof kybDocumentLabels)[]
              ).map((document) => {
                const url = documentUrl(data.documents[document]);
                const label =
                  data.requirements?.documents.find(
                    (item) => item.key === document,
                  )?.label ?? kybDocumentLabels[document];
                return (
                  <li
                    key={document}
                    className="flex flex-wrap justify-between gap-3 py-3 text-sm"
                  >
                    <span>{label}</span>
                    {url ? (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline"
                        aria-label={`Open ${label}`}
                      >
                        Open document ↗
                      </a>
                    ) : (
                      <span className="text-gray-500">Unavailable</span>
                    )}
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-xs text-gray-500">
              Documents open in a new tab. Refresh the review if a link has
              expired.
            </p>
            {sectionActions("business_documents", data.businessDocumentsStatus)}
          </ReviewSection>
          <ReviewSection title="3. Beneficial owners" status={data.uboStatus}>
            <p className="mb-4 text-sm">
              Total declared ownership:{" "}
              <strong>
                {data.owners.reduce(
                  (sum, owner) => sum + Number(owner.ownershipPercentage),
                  0,
                )}
                %
              </strong>
            </p>
            {data.owners.length ? (
              data.owners.map((owner) => (
                <div
                  key={owner.id}
                  className="p-4 mb-4 border border-gray-200 rounded"
                >
                  <h3 className="mb-3 font-medium">
                    {owner.fullName}{" "}
                    {owner.isAccountHolder && (
                      <span className="text-sm font-normal text-gray-500">
                        · Account holder
                      </span>
                    )}
                  </h3>
                  <ReviewFields
                    fields={[
                      ["Ownership", `${owner.ownershipPercentage}%`],
                      ["Email", owner.email],
                      [
                        "Identity",
                        owner.isAccountHolder ? (
                          "Existing account verification"
                        ) : (
                          <StatusBadge status={owner.identityStatus} />
                        ),
                      ],
                      [
                        "Address",
                        owner.isAccountHolder ? (
                          "Existing account verification"
                        ) : (
                          <StatusBadge status={owner.addressStatus} />
                        ),
                      ],
                      ...(owner.inviteSentAt
                        ? [
                            ["Invite sent", dateLabel(owner.inviteSentAt)] as [
                              string,
                              string,
                            ],
                          ]
                        : []),
                    ]}
                  />
                  {canDecide && !owner.isAccountHolder && !decision && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        choose({
                          action: "reject",
                          ownerID: owner.id,
                          ownerName: owner.fullName,
                        })
                      }
                      className="mt-4 text-sm text-red-700 underline disabled:opacity-50"
                    >
                      Reject verification for {owner.fullName}
                    </button>
                  )}
                  {decision &&
                    "ownerID" in decision &&
                    decision.ownerID === owner.id &&
                    canDecide &&
                    decisionForm(decision)}
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500">
                No beneficial owners submitted.
              </p>
            )}
            {sectionActions("ubo", data.uboStatus)}
          </ReviewSection>
          <ReviewSection title="Review history">
            {data.decisions.length ? (
              <ul className="divide-y divide-gray-200">
                {data.decisions.map((entry) => (
                  <li key={entry.id} className="py-3 text-sm">
                    <p className="font-medium">
                      {entry.action === "approved" ? "Approved" : "Rejected"}{" "}
                      {entry.targetType === "ubo_owner"
                        ? `owner ${data.owners.find((owner) => owner.id === entry.uboOwnerID)?.fullName || `#${entry.uboOwnerID}`}`
                        : sectionLabels[entry.targetType]}
                    </p>
                    <p className="mt-1 text-gray-500">
                      {data.auditLogs.find(
                        (log) => log.adminID === entry.adminID,
                      )?.actor?.displayName || `Admin #${entry.adminID}`}{" "}
                      · {dateLabel(entry.createdAt)}
                    </p>
                    {entry.reason && (
                      <p className="mt-2 break-words whitespace-pre-wrap">
                        {entry.reason}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">No review decisions yet.</p>
            )}
          </ReviewSection>
        </>
      ) : (
        <p role="alert">No Tier 4 application is available for this user.</p>
      )}
    </ReviewLayout>
  );
}
